<template>
  <div>
    <label v-if="label" class="value-period-label">
      {{ label }}
      <slot name="label-suffix"></slot>
    </label>
    <div class="value-period-group" :class="{ 'value-period-group--disabled': disabled, 'value-period-group--error': error }">
      <div class="value-period-group__value">
        <el-input-number
          :controls="false"
          :value="value"
          :min="min"
          :step="1"
          :disabled="disabled"
          :placeholder="placeholder"
          @input="onValueChange"
        />
      </div>
      <div class="value-period-group__divider"></div>
      <div class="value-period-group__period">
        <el-select
          :value="period"
          :disabled="disabled"
          @input="onPeriodChange"
        >
          <el-option
            v-for="p in periods"
            :label="p.label"
            :value="p.id"
            :key="p.id"
          />
        </el-select>
      </div>
    </div>
    <div v-if="error" class="value-period-error">{{ error }}</div>
  </div>
</template>

<script>
import { Select, Option, InputNumber } from 'element-ui';

export default {
  name: 'ValuePeriodInput',

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [InputNumber.name]: InputNumber
  },

  props: {
    /** Numeric value (v-model) */
    value: { type: Number, default: 1 },
    /** Selected period id (.sync) */
    period: { type: [Number, String], default: null },
    /** Available period options [{id, label}] */
    periods: { type: Array, required: true },
    /** Minimum numeric value */
    min: { type: Number, default: 0 },
    /** Label above the group */
    label: { type: String, default: null },
    /** Error message */
    error: { type: String, default: null },
    /** Placeholder for number input */
    placeholder: { type: String, default: '0' },
    /** Disabled state */
    disabled: { type: Boolean, default: false }
  },

  methods: {
    onValueChange(val) {
      // el-input-number emits null when cleared
      this.$emit('input', val == null ? 0 : val);
    },

    onPeriodChange(id) {
      this.$emit('update:period', id);
    }
  }
};
</script>

<style lang="scss" scoped>
.value-period-label {
  display: block;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8571em;
  margin-bottom: 5px;
}

.value-period-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: #e14eca;
  }

  &--disabled {
    opacity: 0.6;
  }

  &--error {
    border-color: #ec250d;
  }

  &__value {
    flex: 0 0 90px;
    min-width: 0;

    ::v-deep .el-input-number {
      width: 100%;
    }

    ::v-deep .el-input-number .el-input__inner {
      border: none;
      border-radius: 0;
      background: transparent;
      color: white;
      text-align: right;
      padding: 0 14px;
      height: 40px;
      font-weight: 500;
    }
  }

  &__divider {
    width: 1px;
    background: rgba(255, 255, 255, 0.08);
    flex-shrink: 0;
  }

  &__period {
    flex: 1;
    min-width: 0;

    ::v-deep .el-select {
      width: 100%;
    }

    ::v-deep .el-input__inner {
      border: none;
      border-radius: 0;
      background: transparent;
      color: rgba(255, 255, 255, 0.9);
      height: 40px;
      padding-left: 14px;
      cursor: pointer;
    }

    ::v-deep .el-input__suffix {
      right: 10px;
    }

    ::v-deep .el-input__icon {
      color: #e14eca;
      font-weight: 600;
    }
  }
}

.value-period-error {
  color: #ec250d;
  font-size: 0.75rem;
  margin-top: 4px;
}

// Light mode
.white-content {
  .value-period-label {
    color: rgba(52, 70, 117, 0.7);
  }

  .value-period-group {
    background: #ffffff;
    border-color: #d1d5db;

    &:focus-within {
      border-color: #e14eca;
    }

    &__value ::v-deep .el-input-number .el-input__inner {
      color: #344675;
    }

    &__divider {
      background: rgba(0, 0, 0, 0.08);
    }

    &__period ::v-deep .el-input__inner {
      color: #344675;
    }

    &__period ::v-deep .el-input__icon {
      color: #e14eca;
    }
  }
}
</style>
